// FOC-492 — Paso "Verifica este dispositivo" dentro del flujo de login.
//
// Vive en la misma vista del login porque el token temporal solo está en
// memoria (ver core/infrastructure/security/deviceVerification.js): una
// navegación completa o un refresh lo pierden a propósito.

import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react";
import mailIcon from "@iconify-icons/lucide/mail";
import loaderIcon from "@iconify-icons/lucide/loader-circle";

import {
  clearVerification,
  getVerification,
} from "../../../core/infrastructure/security/deviceVerification";
import {
  resendCode,
  verifyDevice,
} from "../../../core/infrastructure/security/deviceVerificationService";

const CODE_LENGTH = 6;

// Errores del back que mostramos tal cual al usuario; cualquier otro cae
// al genérico "código inválido".
const ERROR_KEYS = {
  CODE_EXPIRED: "deviceVerification.codeExpired",
  CODE_RESEND_LIMIT: "deviceVerification.resendLimit",
  CODE_RESEND_TOO_SOON: "deviceVerification.resendTooSoon",
};

const emptyCode = () => Array(CODE_LENGTH).fill("");

/**
 * Props:
 *   onVerified(data)         — se llama con el payload de /device/verify OK
 *                              (user, token, allowed, menu, ibo, isAdmin).
 *   onBackToLogin(messageKey?) — vuelve al formulario; si messageKey viene,
 *                              el padre decide si mostrarlo (p. ej. timeout).
 */
const VerifyDeviceStep = ({ onVerified, onBackToLogin }) => {
  const { t } = useTranslation("global");
  const [info] = useState(() => getVerification());
  const [code, setCode] = useState(emptyCode);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [now, setNow] = useState(Date.now());
  const inputsRef = useRef([]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const expired = !info || now >= info.expiresAt;
  useEffect(() => {
    if (expired && !blocked) {
      clearVerification();
      onBackToLogin("deviceVerification.timeout");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expired, blocked]);

  if (blocked) {
    return (
      <div>
        <div
          style={{
            background: "rgba(220, 38, 38, 0.08)",
            border: "1px solid var(--color-danger)",
            color: "var(--color-danger)",
            padding: 14,
            borderRadius: 11,
            marginBottom: 16,
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          {t("deviceVerification.blocked")}
        </div>
        <button
          type="button"
          onClick={() => onBackToLogin()}
          className="btn btn-primary w-full"
          style={{ minHeight: 52, fontWeight: 800 }}
        >
          {t("deviceVerification.backToLogin")}
        </button>
      </div>
    );
  }

  if (!info || expired) return null;

  const joined = code.join("");
  const complete = joined.length === CODE_LENGTH && code.every((c) => c !== "");
  const secondsToResend = Math.max(0, Math.ceil((info.resendAt - now) / 1000));
  const canResend = secondsToResend === 0 && !busy;

  const focusInput = (index) => {
    const el = inputsRef.current[index];
    if (el) el.focus();
  };

  const handleChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    setCode((prev) => {
      const next = prev.slice();
      next[index] = digit;
      return next;
    });
    if (digit && index < CODE_LENGTH - 1) focusInput(index + 1);
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) focusInput(index - 1);
    if (e.key === "ArrowLeft" && index > 0) focusInput(index - 1);
    if (e.key === "ArrowRight" && index < CODE_LENGTH - 1) focusInput(index + 1);
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, CODE_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const next = emptyCode();
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setCode(next);
    const nextFocus = Math.min(pasted.length, CODE_LENGTH - 1);
    focusInput(nextFocus);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!complete || busy) return;
    setBusy(true);
    setNotice(null);
    try {
      const data = await verifyDevice(joined);
      onVerified(data);
    } catch (error) {
      const status = error.response?.status;
      const bodyCode = error.response?.data?.code;
      if (status === 401) {
        clearVerification();
        onBackToLogin("deviceVerification.timeout");
        return;
      }
      if (bodyCode === "ACCOUNT_BLOCKED") {
        setBlocked(true);
        return;
      }
      const key = ERROR_KEYS[bodyCode] || "deviceVerification.invalid";
      setNotice({ severity: "danger", text: t(key) });
      setCode(emptyCode());
      focusInput(0);
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;
    setBusy(true);
    setNotice(null);
    try {
      await resendCode();
      setNotice({ severity: "success", text: t("deviceVerification.resent") });
    } catch (error) {
      const status = error.response?.status;
      const bodyCode = error.response?.data?.code;
      if (status === 401) {
        clearVerification();
        onBackToLogin("deviceVerification.timeout");
        return;
      }
      const key = ERROR_KEYS[bodyCode] || "deviceVerification.resendError";
      setNotice({ severity: "danger", text: t(key) });
    } finally {
      setBusy(false);
    }
  };

  const noticeStyle = (severity) => ({
    background:
      severity === "success"
        ? "rgba(34, 197, 94, 0.08)"
        : "rgba(220, 38, 38, 0.08)",
    border: `1px solid ${
      severity === "success" ? "var(--color-success)" : "var(--color-danger)"
    }`,
    color:
      severity === "success" ? "var(--color-success)" : "var(--color-danger)",
    padding: 12,
    borderRadius: 10,
    marginBottom: 16,
    fontWeight: 600,
    fontSize: 13.5,
  });

  const inputStyle = {
    width: 44,
    height: 56,
    textAlign: "center",
    fontSize: 22,
    fontWeight: 800,
    color: "var(--color-ink)",
    background: "var(--color-surface)",
    border: "1.5px solid var(--color-line-strong)",
    borderRadius: 11,
    outline: "none",
  };

  return (
    <div>
      <div style={{ textAlign: "center", marginBottom: 18 }}>
        <div
          style={{
            width: 56,
            height: 56,
            margin: "0 auto 12px",
            borderRadius: "50%",
            background: "rgba(14, 165, 233, 0.12)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--color-accent-text)",
          }}
        >
          <Icon icon={mailIcon} width={26} height={26} aria-hidden="true" />
        </div>
        <h2
          style={{
            fontSize: 20,
            fontWeight: 800,
            margin: "0 0 6px",
            color: "var(--color-ink)",
          }}
        >
          {t("deviceVerification.title")}
        </h2>
        <p
          style={{
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1.5,
            color: "var(--color-ink-2)",
            margin: 0,
          }}
        >
          {t("deviceVerification.subtitle", { email: info.maskedEmail })}
        </p>
      </div>

      {notice && <div style={noticeStyle(notice.severity)}>{notice.text}</div>}

      <form onSubmit={handleSubmit} noValidate>
        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "center",
            marginBottom: 20,
          }}
          onPaste={handlePaste}
        >
          {code.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputsRef.current[index] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              disabled={busy}
              autoFocus={index === 0}
              aria-label={t("deviceVerification.digitLabel", { position: index + 1 })}
              style={inputStyle}
            />
          ))}
        </div>

        <button
          type="submit"
          disabled={!complete || busy}
          className="btn btn-primary w-full flex items-center justify-center gap-s2"
          style={{ minHeight: 52, fontWeight: 800, marginBottom: 14 }}
        >
          {busy && (
            <Icon
              icon={loaderIcon}
              width={18}
              height={18}
              aria-hidden="true"
              style={{ animation: "spin 1s linear infinite" }}
            />
          )}
          {t("deviceVerification.verify")}
        </button>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          <button
            type="button"
            onClick={handleResend}
            disabled={!canResend}
            style={{
              border: 0,
              background: "transparent",
              padding: 0,
              cursor: canResend ? "pointer" : "default",
              color: canResend
                ? "var(--color-accent-text)"
                : "var(--color-ink-2)",
              textDecoration: canResend ? "underline" : "none",
            }}
          >
            {canResend
              ? t("deviceVerification.resend")
              : t("deviceVerification.resendIn", { seconds: secondsToResend })}
          </button>
          <button
            type="button"
            onClick={() => {
              clearVerification();
              onBackToLogin();
            }}
            style={{
              border: 0,
              background: "transparent",
              padding: 0,
              cursor: "pointer",
              color: "var(--color-ink-2)",
              textDecoration: "underline",
            }}
          >
            {t("deviceVerification.backToLogin")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default VerifyDeviceStep;
